import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StudentAssignments } from "../components/Assignments";
import { WeeklyGoal, JoinClassBanner, DigestPreview, StatCards, TopicBests, RecentActivity, MistakesCta, StreakBadges } from "../components/DashboardParts";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [progress, setProgress] = useState(null);
  const [topics, setTopics] = useState([]);
  const [mistakeCount, setMistakeCount] = useState(0);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/me/progress"), api.get("/topics"), api.get("/me/mistakes")])
      .then(([p, t, m]) => {
        setProgress(p.data);
        setTopics(t.data.topics);
        setMistakeCount(m.data.mistakes.length);
      })
      .catch((e) => setErr(apiError(e, "Could not load your progress.")));
  }, [user]);

  if (loading) return <div className="text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const topicMap = Object.fromEntries(topics.map((t) => [t.id, t]));

  return (
    <div className="space-y-10" data-testid="dashboard-root">
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Signed in as {user.email}</div>
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Hi {user.name.split(" ")[0]} 👋</h1>
        <p className="text-slate-400">Here's how you're doing this week.</p>
      </div>

      {err && <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="dashboard-error">{err}</div>}

      <JoinClassBanner />
      <StudentAssignments />

      <Link to="/daily" data-testid="daily-cta" className="flex items-center justify-between rounded-md border border-orange-400/40 bg-gradient-to-r from-orange-500/10 to-red-500/5 p-5 hover:border-orange-400/60">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-300">Today's challenge</div>
          <div className="font-display mt-1 text-lg font-semibold text-white">Answer one question to keep your streak</div>
        </div>
        <div className="font-mono-num text-2xl text-orange-300">→</div>
      </Link>

      <WeeklyGoal />
      <MistakesCta count={mistakeCount} />
      <StatCards progress={progress} />
      <StreakBadges />
      <TopicBests topics={topics} progress={progress} />
      <RecentActivity progress={progress} topicMap={topicMap} />
      <DigestPreview />
    </div>
  );
}
